import React, { useEffect, useMemo, useState } from 'react';
import { useProducts } from '@jbhi-fi/components/src/hooks/useProducts/useProducts';
import { ProductCard } from '@jbhi-fi/components/src/ProductCard';
import { AlgoliaHitTransformer } from '@jbhi-fi/components/src/Algolia/transformers/AlgoliaHitTransformer';
import { useProductActionsContext } from '@jbhi-fi/components/src/context/ProductActionsProvider';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { trackViewItemList } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackViewItemList';
import { transformAlgoliaHitForTracking } from '@jbhi-fi/components/src/Algolia/transformers/transformAlgoliaHitForTracking';
import { styles } from './installProductService.css';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import { OldApplianceRemovalSection } from './OldApplianceRemovalSection';
import { useAlternativesContext } from '@jbhi-fi/components/src/ProductAlternatives/AlternativesContextProvider';
import { InstallAccessorySectionsProps } from './types';
import { parseSkuList, selectPreferredProducts, isProductAvailable, matchesTvScreenSize } from './utils';
import { getStoreCurrency } from '../../../../utils/shopify/getStoreCurrency';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { useInstallServiceDrawer } from './InstallServiceDrawerContext';
import { Tag } from '@jbhi-fi/components/src/ContentCard/Tag/Tag';
import { INSTALL_ID_PREFIX } from '@jbhi-fi/components/src/Attach/ServicesInfoBlock/ServicesInfoBlock';

export const InstallAccessorySections = ({
  variant,
  facets,
  pageType,
  oldApplianceRemovalSuggestion,
  isCartReady,
  selectedOfferingId,
  addRemovalService,
  removeRemovalService,
  getCartLoading,
  onReturnToCartTracking,
}: InstallAccessorySectionsProps) => {
  const [isRemovalLoading, setIsRemovalLoading] = useState(false);
  const isMobile = useBreakpoint('md', 'down') || variant === 'minicart' || variant === 'cart';
  const tvInstall = window.Jbhifi?.installConfig?.tvInstallConfig;
  const ecommerceExtraProps = {
    list: 'TV Installs',
    itemListSlot: variant,
    itemListServiceConfig: window.smartModuleSettings.alternativesGoogleRecModelType,
  };

  const { handleViewAlternatives } = useAlternativesContext();
  const installServiceDrawer = useInstallServiceDrawer();

  const bracketSkus = parseSkuList(tvInstall?.tvBrackets);
  const antennaCableSkus = parseSkuList(tvInstall?.antennaCables);
  const hdmiCableSkus = parseSkuList(tvInstall?.hdmiCables);

  const skusToFetch = Array.from(new Set<string>([...bracketSkus, ...antennaCableSkus, ...hdmiCableSkus]));

  const isBracketRequired = useMemo(() => {
    if (!selectedOfferingId) return false;
    const group = Object.values(tvInstall?.tvInstallMetaField?.tvInstallGroups ?? {}).find(group =>
      group.skus?.includes(selectedOfferingId.replace(INSTALL_ID_PREFIX, ''))
    );
    return !!group?.isBracketRequired;
  }, [selectedOfferingId]);

  const { products: fetchedProducts = [] } = useProducts({
    productSkus: skusToFetch,
    options: { shouldResolveExtraOnlineProductFields: true },
    isEnabled: skusToFetch.length > 0,
  });

  const { onAddToCart, productCartPropsOverrides = {} } = useProductActionsContext();

  const bracketProducts = useMemo(
    () =>
      selectPreferredProducts(
        fetchedProducts,
        bracketSkus,
        product => isProductAvailable(product) && matchesTvScreenSize(product, facets),
        { limit: 1 }
      ),
    [bracketSkus, fetchedProducts, facets]
  );

  const cableProducts = useMemo(() => {
    if (!fetchedProducts.length) return [];

    const preferredCableGroups = [antennaCableSkus, hdmiCableSkus].filter(group => group.length);
    if (!preferredCableGroups.length) return [];

    const selected: Array<AlgoliaProductHit> = [];
    preferredCableGroups.forEach(group => {
      const matches = selectPreferredProducts(fetchedProducts, group, isProductAvailable, { limit: 1 });
      matches.forEach(match => {
        if (!selected.find(product => product.objectID === match.objectID)) {
          selected.push(match);
        }
      });
    });
    return selected;
  }, [antennaCableSkus, fetchedProducts, hdmiCableSkus]);

  const shouldRenderBracket = bracketProducts.length > 0 && isBracketRequired;
  const shouldRenderCables = cableProducts.length > 0;
  const removalOffering = oldApplianceRemovalSuggestion?.offerings?.[0];

  const [isRemovalSelected, setIsRemovalSelected] = useState(false);

  useEffect(() => {
    if (!shouldRenderBracket) return;
    trackViewItemList({
      currency: getStoreCurrency(),
      listName: 'TV Installs',
      itemListSlot: 'slideout',
      itemListServiceConfig: 'tv_brackets',
      viewedItems: transformAlgoliaHitForTracking(bracketProducts),
    });
  }, [shouldRenderBracket]);

  useEffect(() => {
    if (!shouldRenderCables) return;
    trackViewItemList({
      currency: getStoreCurrency(),
      listName: 'TV Installs',
      itemListSlot: 'slideout',
      itemListServiceConfig: 'tv_cables',
      viewedItems: transformAlgoliaHitForTracking(cableProducts),
    });
  }, [shouldRenderCables]);

  const renderAccessoryProducts = (productsToRender: Array<AlgoliaProductHit>, category: 'bracket' | 'cables') => (
    <div className={styles.accessoryProducts({ mobile: isMobile })}>
      {productsToRender.map((product, index) => {
        const key = (product.objectID ?? product.sku ?? `${category}-${index}`).toString();
        return (
          <div key={key} className={styles.product}>
            {category === 'bracket' ? (
              <div className={styles.bestForYourTvTag}>
                <Tag label="Best for Your Tv" palette={{ background: '#00B3DC', text: '#fff' }} />
              </div>
            ) : null}
            <ProductCard
              {...AlgoliaHitTransformer.toProductCardProps(product, {
                trackingDescriptor: {
                  page: pageType,
                  group: 'Tv Installs',
                  category: 'Tv Installs',
                  ecommerceExtraProps: {
                    list: 'Tv Installs',
                    itemListSlot: 'slideout',
                  },
                },
              })}
              {...productCartPropsOverrides}
              variant="minified"
              isDynamicWidth
              shouldHideCartOverlay
              shouldShowReviews
              shouldHideWishlist
              onAddToCart={
                onAddToCart
                  ? (event, _sku, variantId, eventData) =>
                      onAddToCart(event, product, variantId, eventData?.trackingDescriptor)
                  : undefined
              }
              onViewAlternatives={() => handleViewAlternatives(product, 4, 'alternatives', ecommerceExtraProps)}
            />
          </div>
        );
      })}
    </div>
  );

  const handleAddRemoval = async () => {
    if (!oldApplianceRemovalSuggestion?.id || !removalOffering?.id) return;
    if (getCartLoading()) return;
    setIsRemovalLoading(true);
    setIsRemovalSelected(true);
    await addRemovalService(oldApplianceRemovalSuggestion.id, [removalOffering.id]);
    setIsRemovalLoading(false);
  };

  const handleRemoveRemoval = async () => {
    if (!removalOffering) return;
    const identifiers = [removalOffering.id, removalOffering.sku].filter(Boolean);
    if (getCartLoading()) return;
    setIsRemovalLoading(true);
    setIsRemovalSelected(false);
    await removeRemovalService(identifiers);
    setIsRemovalLoading(false);
  };

  const shouldRenderRemoval = !!oldApplianceRemovalSuggestion?.offerings?.length;

  if (!shouldRenderBracket && !shouldRenderCables && !shouldRenderRemoval) {
    return null;
  }

  return (
    <div className={styles.accessoriesContainer({ mobile: isMobile })}>
      <div className={styles.accessoriesInnerContainer}>
        {shouldRenderBracket ? (
          <section className={styles.accessorySection({ type: 'bracket' })}>
            <CalloutFont as="h4" className={styles.accessoryHeading}>
              You&rsquo;ll Need a Bracket
            </CalloutFont>
            {renderAccessoryProducts(bracketProducts, 'bracket')}
          </section>
        ) : null}

        {shouldRenderCables ? (
          <section className={styles.accessorySection({ type: 'cable' })}>
            <CalloutFont as="h4" className={styles.accessoryHeading}>
              Do You Need Longer Cables?
            </CalloutFont>
            <p className={styles.accessorySubheading({ mobile: isMobile })}>
              TVs usually ship with shorter cables. We recommend these for your install.
            </p>
            {renderAccessoryProducts(cableProducts, 'cables')}
          </section>
        ) : null}

        {shouldRenderRemoval ? (
          <OldApplianceRemovalSection
            applianceLabel="TV"
            price={removalOffering?.priceInc || 0}
            isSelected={isRemovalSelected}
            isDisabled={!isCartReady && !isRemovalLoading}
            onSelect={handleAddRemoval}
            onDeselect={handleRemoveRemoval}
            isLoading={isRemovalLoading}
          />
        ) : null}

        {(variant === 'minicart' || variant === 'cart') && (
          <Button
            className={styles.returnButton}
            type="primary"
            label="Return to cart"
            onClick={() => {
              installServiceDrawer?.closeDrawer();
              onReturnToCartTracking();
            }}
            fullWidth
            data-testid={`${variant}-return-to-cart-button`}
          />
        )}
      </div>
    </div>
  );
};
